import { lazy, Suspense, useMemo } from "react";

const DocViewerSectionInner = lazy(() => import("./DocViewerSectionInner"));

/**
 * Extensions safe for in-app Cyntler preview.
 * Text-like types (e.g. .txt) are excluded because Cyntler's TXT renderer
 * can execute crafted content (CVE-2026-30691).
 */
const PREVIEWABLE_EXTENSIONS = new Set([
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "tif",
  "tiff",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
]);

/**
 * Returns a lowercase file extension from a document URI (query/hash stripped).
 *
 * @param {string} uri
 * @return {string}
 */
const getExtensionFromUri = uri => {
  if (!uri || typeof uri !== "string") {
    return "";
  }
  try {
    const path = uri.split(/[?#]/)[0];
    const lastSegment = path.split("/").pop() || "";
    const dot = lastSegment.lastIndexOf(".");
    if (dot < 0) {
      return "";
    }
    return lastSegment.slice(dot + 1).toLowerCase();
  } catch {
    return "";
  }
};

/**
 * Whether a document URI may be opened in the in-app viewer.
 *
 * @param {{uri?: string}|string} doc
 * @return {boolean}
 */
const isPreviewableDocument = doc => {
  const uri = typeof doc === "string" ? doc : doc?.uri;
  const ext = getExtensionFromUri(uri);
  return PREVIEWABLE_EXTENSIONS.has(ext);
};

/**
 * Fallback UI when in-app preview is blocked for security.
 *
 * @param {{uri: string}} props
 */
const PreviewBlockedNotice = ({ uri }) => {
  const fileName = (() => {
    try {
      return decodeURIComponent((uri || "").split(/[?#]/)[0].split("/").pop() || "this file");
    } catch {
      return "this file";
    }
  })();

  return (
    <div className="w-100 border rounded-3 shadow-sm p-8 text-center">
      <p className="fs-6 text-gray-700 mb-4">
        In-app preview is not available for <strong>{fileName}</strong> (text and similar
        formats are blocked for security). Download the file to open it locally.
      </p>
      {uri ? (
        <a
          className="btn btn-sm btn-primary"
          href={uri}
          rel="noopener noreferrer"
          target="_blank"
        >
          Download / open file
        </a>
      ) : null}
    </div>
  );
};

/**
 * Lazy doc preview shell. Loads `@cyntler/react-doc-viewer` only when this mounts
 * and only for allowlisted file types (not .txt / other text renderers).
 *
 * @param {Object} props
 * @param {string} [props.docURL] - Single document URL (builds documents=[{ uri }]).
 * @param {Array<{uri: string}>} [props.documents] - Multi-doc list (e.g. compliance).
 * @param {number} [props.activeDoc] - Index into documents for initial active doc.
 * @param {{uri: string}} [props.initialActiveDocument] - Explicit initial active document.
 * @param {boolean} [props.disableHeader=true] - Hide Cyntler header; false for multi-doc nav.
 */
const DocViewerSection = ({
  docURL,
  documents: documentsProp,
  activeDoc,
  initialActiveDocument,
  disableHeader = true,
}) => {
  const documents = useMemo(() => {
    if (documentsProp?.length) return documentsProp;
    if (docURL) return [{ uri: docURL }];
    return [];
  }, [documentsProp, docURL]);

  const resolvedInitialActive =
    initialActiveDocument ||
    (typeof activeDoc === "number" && documents[activeDoc]
      ? documents[activeDoc]
      : undefined);

  const activeForGate = resolvedInitialActive || documents[0];

  if (!documents.length) return null;

  // Block text-like / unknown types before Cyntler ever loads.
  if (activeForGate && !isPreviewableDocument(activeForGate)) {
    return <PreviewBlockedNotice uri={activeForGate.uri || docURL} />;
  }

  const previewableDocuments = documents.filter(isPreviewableDocument);
  if (!previewableDocuments.length) {
    return <PreviewBlockedNotice uri={documents[0]?.uri || docURL} />;
  }

  const previewInitial =
    resolvedInitialActive && isPreviewableDocument(resolvedInitialActive)
      ? resolvedInitialActive
      : previewableDocuments[0];

  return (
    <Suspense
      fallback={
        <div className="w-100 d-flex justify-content-center align-items-center py-10">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
        </div>
      }
    >
      <DocViewerSectionInner
        documents={previewableDocuments}
        initialActiveDocument={previewInitial}
        disableHeader={disableHeader}
      />
    </Suspense>
  );
};

export { DocViewerSection };
